import { AccessEntitlement as AccessEntitlementType } from "@repo/api/schemas/policiesInventory.schema";
import "./AccessEntitlements.css";

import { Avatar, Box, Icon, Typography } from "@saviynt/design-system";
import { useIntl } from "react-intl";

interface AccessEntitlementsProps {
  accessEntitlements?: AccessEntitlementType[];
}

export default function AccessEntitlements({
  accessEntitlements = [],
}: AccessEntitlementsProps) {
  const intl = useIntl();

  if (!accessEntitlements.length) {
    return (
      <Box className="accessEntitlements">
        <Typography kind="h3">
          {intl.formatMessage({
            description:
              "Title for the access entitlements section in NHI asset sidecar showing permissions and roles",
            defaultMessage: "Access Entitlements",
            id: "teqQq0",
          })}
        </Typography>
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        <Typography kind="body2">-</Typography>
      </Box>
    );
  }

  return (
    <Box
      className="accessEntitlements"
      role="region"
      ariaLabel={intl.formatMessage({
        defaultMessage: "Access entitlements information",
        description:
          "Label for the access entitlements section in NHI asset sidecar showing permissions and roles",
        id: "nN0BTG",
      })}
      tabIndex={0}
    >
      <Typography kind="h3">
        {intl.formatMessage({
          description:
            "Title for the access entitlements section in NHI asset sidecar showing permissions and roles",
          defaultMessage: "Access Entitlements",
          id: "teqQq0",
        })}
      </Typography>
      <div
        className="grid-container"
        role="list"
        aria-label={intl.formatMessage({
          defaultMessage: "List of access entitlements",
          description:
            "Label for the access entitlements section in NHI asset sidecar showing permissions and roles",
          id: "tQGCDD",
        })}
      >
        {accessEntitlements.map((entitlement, index) => {
          const isInactive = entitlement.inactiveFor90Days;
          const iconColor = isInactive
            ? "--color-foreground-coral-subtle"
            : "--color-foreground-secondary-subtle";

          return (
            <div
              className="grid-item"
              data-testid={`grid-item-${index}`}
              key={entitlement.type}
              role="listitem"
              aria-label={intl.formatMessage(
                {
                  id: "vgYeQo",
                  defaultMessage: "{type} entitlement: {role}",
                  description:
                    "Accessible label for each entitlement item in the NHI asset sidecar showing its type and role",
                },
                { type: entitlement.type, role: entitlement.role },
              )}
            >
              <Avatar
                size="medium"
                isSelected={isInactive}
                className={
                  isInactive ? "NHI-avatar-inActive" : "NHI-avatar-active"
                }
                classNames={
                  isInactive
                    ? {
                        selectedBorderClassName: "NHI-avatar-coral-border",
                      }
                    : undefined
                }
              >
                <Icon kind="Entitlement" size="medium" color={iconColor} />
              </Avatar>
              <Box>
                <Typography kind="body2-bold">{entitlement.type}</Typography>
                <Typography
                  kind="body3"
                  color="--color-foreground-neutral-subtle"
                >
                  {entitlement.role}
                </Typography>
                <Typography
                  kind="body2"
                  color={
                    isInactive
                      ? "--color-foreground-critical-subtle"
                      : "--color-foreground-secondary-bold"
                  }
                >
                  {intl.formatMessage(
                    {
                      description:
                        "Label showing whether the NHI asset entitlement has been inactive for 90 days with yes/no status",
                      defaultMessage: "Inactive for 90 days: {status}",
                      id: "bb/QTg",
                    },
                    {
                      status: isInactive
                        ? intl.formatMessage({
                            id: "4vSrGm",
                            defaultMessage: "Yes",
                            description:
                              "Confirmation text indicating the NHI asset entitlement has been inactive for 90 days",
                          })
                        : intl.formatMessage({
                            id: "p3jMl2",
                            defaultMessage: "No",
                            description:
                              "Negative text indicating the NHI asset entitlement has not been inactive for 90 days",
                          }),
                    },
                  )}
                </Typography>
                <Typography
                  kind="body2"
                  color="--color-foreground-secondary-bold"
                >
                  {intl.formatMessage(
                    {
                      id: "Lf4fdB",
                      defaultMessage: "Level: {accessLevel}",
                      description:
                        "Label displaying the access level of the NHI asset entitlement with its value",
                    },
                    {
                      accessLevel: entitlement.accessLevel ?? "",
                    },
                  )}
                </Typography>
              </Box>
            </div>
          );
        })}
      </div>
    </Box>
  );
}
